import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { useThemeContext } from '../../context/ThemeContext';

const navItems = [
    { label: 'Home', path: '/' },
    { label: 'CV', path: '/cv' },
    { label: 'News', path: '/news' },
];

const Header: React.FC = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { isDark, toggleTheme } = useThemeContext();

    const isActive = (path: string) =>
        path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

    const ThemeBtn = (
        <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
            <IconButton
                onClick={toggleTheme}
                sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
                }}
            >
                {isDark ? <LightModeRoundedIcon sx={{ color: '#FCD34D' }} /> : <DarkModeRoundedIcon sx={{ color: '#6366F1' }} />}
            </IconButton>
        </Tooltip>
    );

    return (
        <>
            <AppBar position="sticky" elevation={0} color="transparent">
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ height: 72 }}>
                        <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', color: 'text.primary' }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CodeRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Aleksandr Pashchenko</Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />

                        {!isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {navItems.map(item => (
                                    <Button
                                        key={item.path}
                                        component={RouterLink}
                                        to={item.path}
                                        sx={{
                                            px: 2.5, py: 1, borderRadius: '12px',
                                            color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                                            fontWeight: isActive(item.path) ? 700 : 500,
                                            bgcolor: isActive(item.path) ? (isDark ? 'rgba(96,165,250,0.1)' : 'rgba(37,99,235,0.08)') : 'transparent',
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                ))}
                                <Box sx={{ ml: 1 }}>{ThemeBtn}</Box>
                            </Box>
                        )}

                        {isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {ThemeBtn}
                                <IconButton onClick={() => setDrawerOpen(true)} sx={{ borderRadius: '12px' }}>
                                    <MenuRoundedIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 280, bgcolor: 'background.default' } }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={() => setDrawerOpen(false)} sx={{ borderRadius: '12px' }}><CloseRoundedIcon /></IconButton>
                </Box>
                <List sx={{ px: 2 }}>
                    {navItems.map(item => (
                        <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton component={RouterLink} to={item.path} onClick={() => setDrawerOpen(false)} sx={{ borderRadius: '12px', bgcolor: isActive(item.path) ? (isDark ? 'rgba(96,165,250,0.1)' : 'rgba(37,99,235,0.08)') : 'transparent' }}>
                                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive(item.path) ? 700 : 500, color: isActive(item.path) ? 'primary.main' : 'text.primary' }} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Drawer>
        </>
    );
};

export default Header;
